// Batch BC — Dashboard "Refer Friends" widget.
//
// Persistent fixture on the authenticated Dashboard that closes the
// viral loop for existing users. Renders above the Recent Deals table
// (horizontal card spanning the same width). It is intentionally more
// subtle than the post-signup WaitlistSuccess screen — this is a fixture
// the user sees on every visit, not a one-time conversion moment.
//
//   • Init call → GET /api/me/referral-code (one-shot, mints code)
//   • Live tally → GET /api/me/referral-stats every 30s + on focus
//   • Copy-link → POSIX clipboard with execCommand fallback
//   • Share intents → X / LinkedIn / WhatsApp / Email via referralShare.js
//   • Unlocked at 3 referred signups → switches to emerald variant
//
// All network errors are silent so the share UX never breaks if a
// poll round-trips fails. PostHog events fire via the `dashboard_*`
// trackers in `lib/landingEvents.js` so the funnel can attribute
// surface-of-origin separately from the homepage success screen.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Copy as CopyIcon, Gift, Sparkles } from "lucide-react";
import {
  trackDashboardReferralLinkCopied,
  trackDashboardReferralShareClicked,
  trackDashboardReferralGoalUnlocked,
} from "@/lib/landingEvents";
import {
  REWARD_TARGET,
  SHARE_CHANNELS,
  SHARE_MESSAGE_DASHBOARD,
  buildShareLink,
  shareIntentUrl,
  channelLabel,
} from "@/lib/referralShare";

const POLL_INTERVAL_MS = 30_000;

function _debug(...args) {
  if (typeof console !== "undefined" && console.debug) {
    try { console.debug(...args); } catch { /* ignore */ }
  }
}

export default function ReferFriendsWidget() {
  const [code, setCode] = useState(null);
  const [shareUrl, setShareUrl] = useState("");
  const [stats, setStats] = useState({
    referred_count: 0,
    unlocked: false,
    target: REWARD_TARGET,
  });
  const [copied, setCopied] = useState(false);
  const [ready, setReady] = useState(false);
  const unlockedFiredRef = useRef(false);
  const copyTimerRef = useRef(null);

  // Local fallback in case the server didn't provide a share_url (origin
  // matches anyway on the same host); buildShareLink uses window.origin.
  const localShareLink = useMemo(() => buildShareLink(code), [code]);
  const effectiveShareLink = shareUrl || localShareLink;

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get("/me/referral-stats");
      const body = res.data || {};
      const next = body.stats || {};
      _debug("[dashboard-referral-poll]", {
        code: body.referral_code,
        referred_count: next.referred_count,
        unlocked: next.unlocked,
      });
      // The poll endpoint also returns the (cached) code; adopt it so a
      // user who logged in on a new device still gets the right link
      // without an extra round-trip.
      if (body.referral_code && body.referral_code !== code) {
        setCode(body.referral_code);
      }
      setStats((prev) => {
        const merged = {
          referred_count: Number(next.referred_count ?? prev.referred_count ?? 0),
          unlocked: Boolean(next.unlocked),
          target: Number(next.target ?? prev.target ?? REWARD_TARGET),
        };
        if (
          merged.unlocked &&
          !unlockedFiredRef.current &&
          merged.referred_count >= merged.target
        ) {
          unlockedFiredRef.current = true;
          trackDashboardReferralGoalUnlocked({
            referral_code: body.referral_code || code,
            referred_count: merged.referred_count,
          });
        }
        return merged;
      });
    } catch (e) {
      _debug("[dashboard-referral-poll] error", e);
      /* preserve last-known state */
    }
  }, [code]);

  // Init: fetch the code + initial stats in a single round-trip.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/me/referral-code");
        const body = res.data || {};
        if (cancelled) return;
        setCode(body.referral_code || null);
        setShareUrl(body.share_url || "");
        const s = body.stats || {};
        const merged = {
          referred_count: Number(s.referred_count ?? 0),
          unlocked: Boolean(s.unlocked),
          target: Number(s.target ?? REWARD_TARGET),
        };
        setStats(merged);
        if (merged.unlocked && merged.referred_count >= merged.target) {
          unlockedFiredRef.current = true;
        }
      } catch (e) {
        _debug("[dashboard-referral-init] error", e);
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Poll every 30s + refresh on focus / tab visibility change so the
  // count ticks up the moment a friend signs up.
  useEffect(() => {
    if (!code) return undefined;
    const id = window.setInterval(fetchStats, POLL_INTERVAL_MS);
    const onFocus = () => fetchStats();
    const onVisibility = () => {
      if (typeof document !== "undefined" && document.visibilityState === "visible") {
        fetchStats();
      }
    };
    window.addEventListener("focus", onFocus);
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibility);
    }
    return () => {
      window.clearInterval(id);
      window.removeEventListener("focus", onFocus);
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibility);
      }
    };
  }, [code, fetchStats]);

  useEffect(() => () => {
    if (copyTimerRef.current) window.clearTimeout(copyTimerRef.current);
  }, []);

  const handleCopy = async () => {
    if (!effectiveShareLink) return;
    try {
      await navigator.clipboard.writeText(effectiveShareLink);
      setCopied(true);
      trackDashboardReferralLinkCopied({ referral_code: code });
      if (copyTimerRef.current) window.clearTimeout(copyTimerRef.current);
      copyTimerRef.current = window.setTimeout(() => setCopied(false), 2000);
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = effectiveShareLink;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        setCopied(true);
        trackDashboardReferralLinkCopied({ referral_code: code });
        copyTimerRef.current = window.setTimeout(() => setCopied(false), 2000);
      } catch { /* clipboard blocked — give up silently */ }
    }
  };

  const handleShare = (channel) => {
    if (!effectiveShareLink) return;
    trackDashboardReferralShareClicked({ channel, referral_code: code });
    if (typeof window !== "undefined") {
      window.open(
        shareIntentUrl(channel, effectiveShareLink, {
          message: SHARE_MESSAGE_DASHBOARD,
        }),
        "_blank",
        "noopener,noreferrer",
      );
    }
  };

  // Don't render until the init call resolves — avoids a flash of an
  // empty input that the user could accidentally click before the
  // share link populates. The init call usually resolves under 100ms.
  if (!ready) return null;

  const count = Math.max(0, stats.referred_count || 0);
  const target = Math.max(1, stats.target || REWARD_TARGET);
  const progressPct = Math.min(100, Math.round((count / target) * 100));
  const remaining = Math.max(0, target - count);
  const isUnlocked = count >= target;

  let rewardCopy;
  if (isUnlocked) {
    rewardCopy = "Unlocked — your friends are getting early access.";
  } else if (count === 0) {
    rewardCopy = `Invite ${target} friends and you'll all get early access perks.`;
  } else {
    rewardCopy = `${remaining} more to unlock early-access perks for you and them.`;
  }

  return (
    <div
      data-testid="refer-friends-widget"
      className={
        "rounded-2xl border p-5 shadow-sm transition-colors " +
        (isUnlocked
          ? "border-emerald-200 bg-emerald-50/40"
          : "border-indigo-200 bg-white")
      }
    >
      <div className="grid gap-5 md:grid-cols-[1fr_auto] md:items-start">
        {/* Header + reward copy */}
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={
                "inline-flex h-7 w-7 items-center justify-center rounded-lg " +
                (isUnlocked
                  ? "bg-emerald-100 text-emerald-600"
                  : "bg-indigo-100 text-indigo-600")
              }
            >
              {isUnlocked ? (
                <Sparkles className="h-4 w-4" data-testid="refer-friends-icon-unlocked" />
              ) : (
                <Gift className="h-4 w-4" data-testid="refer-friends-icon" />
              )}
            </span>
            <h3 className="text-base font-semibold text-slate-900">Refer friends</h3>
            <span
              className={
                "ml-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider " +
                (isUnlocked
                  ? "bg-emerald-100 text-emerald-700"
                  : "bg-indigo-50 text-indigo-700")
              }
              data-testid="refer-friends-progress-count"
            >
              {count} of {target}
            </span>
          </div>
          <p
            className={
              "mt-2 text-sm " + (isUnlocked ? "text-emerald-800" : "text-slate-600")
            }
            data-testid="refer-friends-reward-copy"
          >
            {rewardCopy}
          </p>

          {/* Share link row */}
          <div className="mt-4 flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 p-1.5">
            <input
              readOnly
              value={effectiveShareLink}
              className="flex-1 truncate bg-transparent px-2 py-1.5 text-xs text-slate-700 outline-none sm:text-sm"
              data-testid="refer-friends-link-input"
              aria-label="Your referral link"
              onFocus={(e) => e.target.select()}
            />
            <Button
              type="button"
              size="sm"
              onClick={handleCopy}
              disabled={!effectiveShareLink}
              className={
                copied
                  ? "bg-emerald-600 text-white hover:bg-emerald-700"
                  : "bg-indigo-600 text-white hover:bg-indigo-700"
              }
              data-testid="refer-friends-copy-button"
              aria-label={copied ? "Link copied" : "Copy referral link"}
            >
              <CopyIcon className="mr-1 h-3.5 w-3.5" />
              {copied ? "Copied!" : "Copy"}
            </Button>
          </div>

          {/* Progress bar */}
          <div
            className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-slate-100"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={target}
            aria-valuenow={count}
            data-testid="refer-friends-progress-track"
          >
            <div
              className={
                "h-full rounded-full transition-all duration-500 " +
                (isUnlocked ? "bg-emerald-500" : "bg-indigo-600")
              }
              style={{ width: `${progressPct}%` }}
              data-testid="refer-friends-progress-bar"
            />
          </div>
        </div>

        {/* Share channels (stacked into 2x2 on mobile, single row on md+) */}
        <div
          className="grid grid-cols-4 gap-2 md:grid-cols-2 md:gap-1.5"
          data-testid="refer-friends-share-row"
        >
          {SHARE_CHANNELS.map(({ id, label, Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => handleShare(id)}
              aria-label={label}
              data-testid={`refer-friends-share-${id}`}
              className="flex flex-col items-center gap-1 rounded-xl border border-slate-200 bg-white px-3 py-2 text-[11px] font-medium text-slate-700 transition hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-200 md:flex-row md:gap-2 md:px-2.5 md:py-1.5 md:text-xs"
            >
              <Icon className="h-3.5 w-3.5" aria-hidden="true" />
              <span>{channelLabel(id)}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
